import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Power } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const Toggle = ({ on, onClick, busy }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    disabled={busy}
    onClick={onClick}
    className={`w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 relative focus-ring disabled:opacity-50
      ${on ? 'bg-zip-red' : 'bg-zip-border'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
      ${on ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

const ModulesFeatures = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [busyKey, setBusyKey]   = useState(null);

  const fetchFeatures = async () => {
    try {
      const res = await api.get('/access/features');
      setFeatures(res.data.data);
    } catch {
      toast.error('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFeatures(); }, []);

  const toggle = async (f) => {
    setBusyKey(f.module);
    const next = !f.enabled;
    // optimistic update
    setFeatures((prev) => prev.map((x) => x.module === f.module ? { ...x, enabled: next } : x));
    try {
      await api.put(`/access/features/${f.module}`, { enabled: next });
      toast.success(`${f.label} ${next ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setFeatures((prev) => prev.map((x) => x.module === f.module ? { ...x, enabled: f.enabled } : x));
      toast.error(err.response?.data?.message || 'Failed to update module');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="zip-card px-5 py-4">
        <p className="text-sm text-zip-muted">
          Turn whole modules on or off across the portal. A disabled module is hidden from every role's
          navigation and its API is blocked — except for the Super Admin.
        </p>
      </div>

      {loading ? (
        <div className="zip-card p-5 space-y-3">
          {[...Array(6)].map((_, i) => <div key={i} className="h-12 skeleton rounded" />)}
        </div>
      ) : (
        <div className="zip-card divide-y divide-zip-border">
          {features.map((f) => (
            <motion.div key={f.module} layout className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border
                  ${f.enabled ? 'bg-zip-red/10 border-zip-red/20 text-zip-red' : 'bg-zip-hover border-zip-border text-zip-muted'}`}>
                  <Power size={16} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-zip-text">{f.label}</div>
                  <div className="text-[11px] font-mono text-zip-muted">{f.module} · {f.enabled ? 'Enabled' : 'Disabled'}</div>
                </div>
              </div>
              <Toggle on={f.enabled} busy={busyKey === f.module} onClick={() => toggle(f)} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModulesFeatures;
